import type { ReactElement } from "react";
import { toast } from "sonner";
import { TOAST_DURATION } from "@/shared/utils/toast";

export type MutationAction = {
	id: string;
	execute: () => Promise<void>;
	rollback: (() => void) | null;
	toast: {
		loading: string | (() => ReactElement);
		success: string;
		error: string | ((err: unknown) => string);
	};
	onSuccess?: () => void;
	onError?: () => void;
};

const LOADING_DELAY_MS = 200;

export function createAction(
	action: Omit<MutationAction, "id">,
): MutationAction {
	return { ...action, id: crypto.randomUUID() };
}

export function createGroupedAction(
	actions: MutationAction[],
	toastCopy: MutationAction["toast"],
): MutationAction {
	return createAction({
		execute: async () => {
			const results = await Promise.allSettled(
				actions.map((action) => action.execute()),
			);
			const errors: unknown[] = [];
			results.forEach((result, index) => {
				const action = actions[index];
				if (result.status === "fulfilled") {
					action.onSuccess?.();
					return;
				}
				action.rollback?.();
				action.onError?.();
				errors.push(result.reason);
			});
			if (errors.length > 0) {
				throw new Error(
					`${errors.length} timekeeper operation${errors.length === 1 ? "" : "s"} failed`,
				);
			}
		},
		rollback: null,
		toast: toastCopy,
	});
}

export async function runMutationAction(action: MutationAction): Promise<void> {
	let settled = false;
	let loadingShown = false;
	const isCustomLoading = typeof action.toast.loading !== "string";

	const loadingTimer = window.setTimeout(() => {
		if (!settled) {
			loadingShown = true;
			showLoading(action);
		}
	}, LOADING_DELAY_MS);

	try {
		await action.execute();
		settled = true;
		window.clearTimeout(loadingTimer);
		showSuccess(action, isCustomLoading, loadingShown);
		action.onSuccess?.();
	} catch (err) {
		settled = true;
		window.clearTimeout(loadingTimer);
		action.rollback?.();
		showError(action, err, isCustomLoading, loadingShown);
		action.onError?.();
		throw err;
	}
}

function showLoading(action: MutationAction) {
	const { loading } = action.toast;
	if (typeof loading === "string") {
		toast.loading(loading, { id: action.id });
		return;
	}
	toast.custom(loading, {
		id: action.id,
		duration: Infinity,
		position: "top-center",
	});
}

function showSuccess(
	action: MutationAction,
	isCustomLoading: boolean,
	loadingShown: boolean,
) {
	if (isCustomLoading) {
		if (loadingShown) toast.dismiss(action.id);
		toast.success(action.toast.success, {
			duration: TOAST_DURATION.success,
		});
		return;
	}
	toast.success(action.toast.success, {
		id: action.id,
		duration: TOAST_DURATION.success,
	});
}

function showError(
	action: MutationAction,
	err: unknown,
	isCustomLoading: boolean,
	loadingShown: boolean,
) {
	if (isCustomLoading && loadingShown) toast.dismiss(action.id);
	toast.error(
		typeof action.toast.error === "function"
			? action.toast.error(err)
			: action.toast.error,
		{
			...(isCustomLoading ? {} : { id: action.id }),
			duration: TOAST_DURATION.error,
			description: err instanceof Error ? err.message : undefined,
		},
	);
}
